import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package, Plus, Edit2, Trash2, Search, Filter } from 'lucide-react'
import { useInventoryStore } from '../store/inventoryStore'

export default function ProductCatalogPage() {
  const navigate = useNavigate()
  const products = useInventoryStore((state) => state.products)
  const addProduct = useInventoryStore((state) => state.addProduct)
  const updateProduct = useInventoryStore((state) => state.updateProduct)
  const deleteProduct = useInventoryStore((state) => state.deleteProduct)

  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    quantity: 0,
    reorderLevel: 0,
    unitPrice: 0,
    supplier: '',
    status: 'in_stock'
  })

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.sku.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesFilter = filterStatus === 'all' || product.status === filterStatus
      return matchesSearch && matchesFilter
    })
  }, [products, searchTerm, filterStatus])

  const handleAddProduct = () => {
    if (formData.name && formData.sku) {
      addProduct(formData)
      setFormData({
        name: '',
        sku: '',
        category: '',
        quantity: 0,
        reorderLevel: 0,
        unitPrice: 0,
        supplier: '',
        status: 'in_stock'
      })
      setShowAddModal(false)
    }
  }

  const handleEditProduct = (product) => {
    setSelectedProduct(product.id)
    setFormData(product)
    setShowAddModal(true)
  }

  const handleUpdateProduct = () => {
    if (selectedProduct && formData.name && formData.sku) {
      updateProduct(selectedProduct, formData)
      setFormData({
        name: '',
        sku: '',
        category: '',
        quantity: 0,
        reorderLevel: 0,
        unitPrice: 0,
        supplier: '',
        status: 'in_stock'
      })
      setSelectedProduct(null)
      setShowAddModal(false)
    }
  }

  const handleDeleteProduct = (id) => {
    if (confirm('Are you sure you want to delete this product?')) {
      deleteProduct(id)
    }
  }

  const closeModal = () => {
    setShowAddModal(false)
    setSelectedProduct(null)
    setFormData({
      name: '',
      sku: '',
      category: '',
      quantity: 0,
      reorderLevel: 0,
      unitPrice: 0,
      supplier: '',
      status: 'in_stock'
    })
  }

  const getStatusColor = (status) => {
    switch(status) {
      case 'in_stock': return 'bg-green-500/20 text-green-300'
      case 'low_stock': return 'bg-yellow-500/20 text-yellow-300'
      case 'out_of_stock': return 'bg-red-500/20 text-red-300'
      default: return 'bg-blue-500/20 text-blue-300'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button onClick={() => navigate(-1)} className="text-blue-400 hover:text-blue-300">←</button>
            <h1 className="text-4xl font-bold text-white flex items-center space-x-3">
              <Package className="w-8 h-8 text-blue-400" />
              <span>Product Catalog</span>
            </h1>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            <span>Add Product</span>
          </button>
        </div>

        {/* Search & Filter */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-blue-400" />
            <input
              type="text"
              placeholder="Search by name or SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-800 border border-blue-500/20 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-blue-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="flex-1 bg-slate-800 border border-blue-500/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-400/50"
            >
              <option value="all">All Status</option>
              <option value="in_stock">In Stock</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-blue-500/20">
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">SKU</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Product Name</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Category</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Quantity</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Unit Price</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Status</th>
                <th className="text-left py-3 px-4 text-blue-300 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id} className="border-b border-blue-500/10 hover:bg-blue-500/5 transition-all">
                  <td className="py-3 px-4 text-white font-mono text-sm">{product.sku}</td>
                  <td className="py-3 px-4 text-white">{product.name}</td>
                  <td className="py-3 px-4 text-blue-300">{product.category}</td>
                  <td className="py-3 px-4 text-white font-semibold">{product.quantity}</td>
                  <td className="py-3 px-4 text-green-400">${product.unitPrice.toFixed(2)}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${getStatusColor(product.status)}`}>
                      {product.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4 flex items-center space-x-2">
                    <button
                      onClick={() => handleEditProduct(product)}
                      className="text-blue-400 hover:text-blue-300 transition-all p-2 hover:bg-blue-500/10 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product.id)}
                      className="text-red-400 hover:text-red-300 transition-all p-2 hover:bg-red-500/10 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                {selectedProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Product Name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="text"
                  placeholder="SKU"
                  value={formData.sku}
                  onChange={(e) => setFormData({...formData, sku: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="text"
                  placeholder="Category"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="Quantity"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value)})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="Reorder Level"
                  value={formData.reorderLevel}
                  onChange={(e) => setFormData({...formData, reorderLevel: parseInt(e.target.value)})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="Unit Price"
                  value={formData.unitPrice}
                  onChange={(e) => setFormData({...formData, unitPrice: parseFloat(e.target.value)})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="text"
                  placeholder="Supplier"
                  value={formData.supplier}
                  onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-400/50"
                >
                  <option value="in_stock">In Stock</option>
                  <option value="low_stock">Low Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={closeModal}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={selectedProduct ? handleUpdateProduct : handleAddProduct}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all"
                >
                  {selectedProduct ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
